import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isPopulated, partitionThresholds } from "./thresholds";

function ThresholdRow({ row, onChange, surfaceSx }) {
  const missing = row.required && !isPopulated(row.threshold_value);
  return (
    <Box p={2} borderRadius="16px" sx={surfaceSx}>
      <TextField
        label={`${row.parameter_label}${row.unit ? ` (${row.unit})` : ""}${row.required ? " *" : ""}`}
        value={row.threshold_value ?? ""}
        onChange={(event) => onChange({ threshold_value: event.target.value })}
        error={missing}
        helperText={missing ? "Required - enter a value" : undefined}
        fullWidth
        sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
      />
      <Box display="flex" flexWrap="wrap" gap={2}>
        <FormControlLabel
          label="Alert enabled"
          control={<Checkbox checked={Boolean(row.alert_enabled)} onChange={(e) => onChange({ alert_enabled: e.target.checked })} />}
        />
        <FormControlLabel
          label="Event enabled"
          control={<Checkbox checked={Boolean(row.event_enabled)} onChange={(e) => onChange({ event_enabled: e.target.checked })} />}
        />
      </Box>
    </Box>
  );
}

// Populated and required thresholds stay visible; empty optional ones are
// collapsed. Collapsing is display-only and never edits values or flags.
export default function ThresholdFields({ rows, onChange, surfaceSx, emptyMessage = "No alert levels are configured for this device model." }) {
  const [expanded, setExpanded] = useState(false);
  if (!rows?.length) return <Alert severity="info" sx={{ borderRadius: "12px" }}>{emptyMessage}</Alert>;
  const { primary, optional, conflicts } = partitionThresholds(rows);
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {conflicts.length > 0 && (
        <Alert severity="warning" sx={{ borderRadius: "12px" }}>
          {conflicts.length} required threshold{conflicts.length > 1 ? "s are" : " is"} hidden by the device model settings and
          {" "}shown here so it can be completed: {conflicts.map((entry) => entry.row.parameter_label).join(", ")}.
        </Alert>
      )}
      {primary.map(({ row, index }) => (
        <ThresholdRow key={row.key} row={row} onChange={(patch) => onChange(index, patch)} surfaceSx={surfaceSx} />
      ))}
      {primary.length === 0 && (
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          No thresholds have values yet.
        </Typography>
      )}
      {optional.length > 0 && (
        <Box>
          <Button
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded ? "true" : "false"}
            sx={{ textTransform: "none", fontWeight: 700, px: 0 }}
          >
            {expanded
              ? `Hide additional thresholds (${optional.length})`
              : `Additional thresholds (optional) - show ${optional.length}`}
          </Button>
          <Collapse in={expanded} unmountOnExit>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              {optional.map(({ row, index }) => (
                <ThresholdRow key={row.key} row={row} onChange={(patch) => onChange(index, patch)} surfaceSx={surfaceSx} />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
